// Music Editor Data Types
// Based on music_editor_requirements.txt specifications

export interface Song {
  _id: string;
  title: string;
  artist?: string;
  key: string;
  capo: number;
  tempo?: number;
  sections: SongSection[];
  createdBy: string;
  addedBy?: string;
  createdAt: Date;
  modifiedAt: Date;
  libraryType?: 'personal' | 'group';
  groupId?: string;
  jamGroupId?: string;
  sourceSongId?: string;
  vocalAssignments?: VocalAssignment[];
  // Local state fields
  isLocalDraft?: boolean;
  lastSyncedAt?: Date;
  importMetadata?: ImportMetadata;
}

export interface SongSection {
  id: string;
  name: string; // "Verse", "Chorus", "Bridge", "Intro", "Outro", "Solo"
  lines: SongLine[];
  collapsed?: boolean; // UI state for section collapse/expand
  order: number; // For section ordering
}

export interface SongLine {
  id: string;
  lyrics: string;
  chords: ChordPlacement[];
}

export interface VocalAssignment {
  id?: string;
  sectionId: string;
  lineId: string;
  startChar: number;
  endChar: number;
  assigneeType: 'member' | 'everyone';
  userId?: string;
}

export interface ChordPlacement {
  id: string;
  chord: string; // "C", "Am", "F#m7", etc.
  position: number; // character position in lyrics (0-based)
}

export interface DragState {
  isDragging: boolean;
  draggedChordId: string | null;
  draggedChordLineId: string | null;
  dragPreviewPosition: { x: number; y: number } | null;
  targetLineId: string | null;
  targetPosition: number | null;
  originalPosition: number | null;
}

// Import related types
export interface ImportMetadata {
  sourceFile?: string;
  mimeType?: string;
  fileSize?: number;
  importedAt?: Date;
  processingTime?: number;
  importMethod?:
    | 'manual'
    | 'gemini-vision-api'
    | 'ocr-fallback'
    | 'openrouter'
    | 'pdf-text-parser';
  confidence?: number; // 0-1
  provider?: string | null;
  providerModels?: {
    content?: string;
    placement?: string;
  } | null;
  keyDetectionMethod?: string;
  keyDetectionConfidence?: number;
}

export interface PdfImportDiagnostics {
  fileName?: string;
  pageCount?: number | null;
  textLength?: number;
  chordLineCount?: number;
  sectionHeaderCount?: number;
  canUseTextPath?: boolean;
}

export interface ValidationError {
  field: string;
  message: string;
  severity: 'error' | 'warning' | 'info';
  suggestedFix?: string;
}

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
  warnings: ValidationError[];
  suggestions: string[];
  confidence: number; // 0-1
  summary: {
    totalErrors: number;
    totalWarnings: number;
    sectionsCount: number;
    chordsCount: number;
    linesCount: number;
  };
}

export interface ImportState {
  isImporting: boolean;
  showImportModal: boolean;
  progress: number; // 0-100
  currentFile: File | null;
  importResult: Song | null;
  validation: ValidationResult | null;
  metadata: ImportMetadata | null;
  diagnostics: {
    pdfStrategy?: string;
    pdfImageFallbackUsed?: boolean;
    pdfTextPathUsed?: boolean;
    aiInputMimeType?: string;
    pdfDiagnostics?: PdfImportDiagnostics | null;
  } | null;
  error: string | null;
  isRetryable: boolean;
  retryDelay: number;
  retryAttempt: number;
  maxRetries: number;
}

export interface EditorState {
  mode: 'lyrics' | 'chords';
  currentSong: Song | null;
  selectedSectionId: string | null;
  selectedLineId: string | null;
  selectedChordId: string | null;
  isEditing: boolean;
  isDirty: boolean; // Has unsaved changes
  lastSaved: Date | null;
  // UI state
  showChordPicker: boolean;
  showSectionManager: boolean;
  // Import state
  importState: ImportState;
  // Drag and drop state
  dragState: DragState;
  // History for undo/redo
  history: Song[];
  historyIndex: number;
  maxHistorySize: number;
}

// Common chord types for chord picker
export interface ChordLibrary {
  major: string[];
  minor: string[];
  seventh: string[];
  minorSeventh: string[];
  suspended: string[];
  extended: string[];
  diminished: string[];
  augmented: string[];
  recent: string[];
  favorites: string[];
}

// Voice dictation types
export interface VoiceRecognitionState {
  isRecording: boolean;
  isProcessing: boolean;
  transcript: string;
  error: string | null;
}

// Enhanced voice recognition types
export interface EnhancedVoiceRecognitionState {
  isSupported: boolean;
  isRecording: boolean;
  isProcessing: boolean;
  transcript: string;
  interimTranscript: string;
  confidence: number;
  error: string | null;
  browserType: 'chrome' | 'safari' | 'firefox' | 'edge' | 'other';
  permissionStatus: 'granted' | 'denied' | 'prompt' | 'unknown';
  isPermissionRequested: boolean;
}

// Song metadata for lists and navigation
export interface SongMetadata {
  _id: string;
  title: string;
  artist?: string;
  key: string;
  modifiedAt: Date;
  isLocalDraft: boolean;
  libraryType?: 'personal' | 'group';
  groupId?: string | null;
}

// Actions for state management
export type EditorAction =
  | { type: 'SET_MODE'; payload: 'lyrics' | 'chords' }
  | { type: 'LOAD_SONG'; payload: Song }
  | { type: 'UPDATE_SONG_TITLE'; payload: string }
  | { type: 'UPDATE_SONG_KEY'; payload: string }
  | { type: 'UPDATE_SONG_CAPO'; payload: number }
  | { type: 'UPDATE_SONG_TEMPO'; payload: number | undefined }
  | { type: 'ADD_SECTION'; payload: { name: string; position?: number } }
  | {
      type: 'UPDATE_SECTION';
      payload: { sectionId: string; updates: Partial<SongSection> };
    }
  | { type: 'DELETE_SECTION'; payload: string }
  | {
      type: 'REORDER_SECTIONS';
      payload: { fromIndex: number; toIndex: number };
    }
  | {
      type: 'ADD_LINE';
      payload: { sectionId: string; lyrics: string; position?: number };
    }
  | { type: 'UPDATE_LINE_LYRICS'; payload: { lineId: string; lyrics: string } }
  | { type: 'REPLACE_VOCAL_ASSIGNMENTS'; payload: VocalAssignment[] }
  | { type: 'DELETE_LINE'; payload: string }
  | {
      type: 'ADD_CHORD';
      payload: { lineId: string; chord: string; position: number };
    }
  | {
      type: 'UPDATE_CHORD';
      payload: { chordId: string; chord?: string; position?: number };
    }
  | { type: 'DELETE_CHORD'; payload: string }
  | { type: 'SELECT_SECTION'; payload: string | null }
  | { type: 'SELECT_LINE'; payload: string | null }
  | { type: 'SELECT_CHORD'; payload: string | null }
  | { type: 'SET_EDITING'; payload: boolean }
  | { type: 'SET_DIRTY'; payload: boolean }
  | { type: 'SAVE_SONG' }
  | { type: 'UNDO' }
  | { type: 'REDO' }
  | { type: 'TOGGLE_CHORD_PICKER'; payload?: boolean }
  | { type: 'TOGGLE_SECTION_MANAGER'; payload?: boolean }
  | {
      type: 'START_CHORD_DRAG';
      payload: {
        chordId: string;
        lineId: string;
        position: { x: number; y: number };
        originalPosition: number;
      };
    }
  | {
      type: 'UPDATE_CHORD_DRAG';
      payload: {
        position: { x: number; y: number };
        targetLineId?: string;
        targetPosition?: number;
      };
    }
  | {
      type: 'END_CHORD_DRAG';
      payload: { success: boolean; newLineId?: string; newPosition?: number };
    }
  | { type: 'CANCEL_CHORD_DRAG' }
  // Import actions
  | { type: 'TOGGLE_IMPORT_MODAL'; payload?: boolean }
  | { type: 'START_IMPORT'; payload: { file: File } }
  | { type: 'UPDATE_IMPORT_PROGRESS'; payload: { progress: number } }
  | {
      type: 'IMPORT_SUCCESS';
      payload: {
        song: Song;
        validation: ValidationResult;
        metadata: ImportMetadata;
        diagnostics?: ImportState['diagnostics'];
      };
    }
  | {
      type: 'IMPORT_ERROR';
      payload: { error: string; isRetryable: boolean; retryDelay: number };
    }
  | { type: 'RESET_IMPORT' }
  | { type: 'RETRY_IMPORT' }
  | { type: 'ACCEPT_IMPORT'; payload?: Song }
  | { type: 'CANCEL_IMPORT' };

// Default song template
export const createEmptySong = (userId: string): Song => ({
  _id: `local_${Date.now()}`,
  title: 'Untitled Song',
  artist: '',
  key: 'C',
  capo: 0,
  tempo: 120,
  sections: [
    {
      id: `section_${Date.now()}`,
      name: 'Verse',
      lines: [
        {
          id: `line_${Date.now()}`,
          lyrics: '',
          chords: [],
        },
      ],
      collapsed: false,
      order: 0,
    },
  ],
  createdBy: userId,
  vocalAssignments: [],
  createdAt: new Date(),
  modifiedAt: new Date(),
  isLocalDraft: true,
});

// Default import state
export const createDefaultImportState = (): ImportState => ({
  isImporting: false,
  showImportModal: false,
  progress: 0,
  currentFile: null,
  importResult: null,
  validation: null,
  metadata: null,
  diagnostics: null,
  error: null,
  isRetryable: false,
  retryDelay: 0,
  retryAttempt: 0,
  maxRetries: 3,
});

// Default editor state
export const createDefaultEditorState = (): EditorState => ({
  mode: 'lyrics',
  currentSong: null,
  selectedSectionId: null,
  selectedLineId: null,
  selectedChordId: null,
  isEditing: false,
  isDirty: false,
  lastSaved: null,
  showChordPicker: false,
  showSectionManager: false,
  importState: createDefaultImportState(),
  dragState: {
    isDragging: false,
    draggedChordId: null,
    draggedChordLineId: null,
    dragPreviewPosition: null,
    targetLineId: null,
    targetPosition: null,
    originalPosition: null,
  },
  history: [],
  historyIndex: -1,
  maxHistorySize: 50,
});

// Accessibility types
export interface KeyboardShortcut {
  key: string;
  modifiers: ('ctrl' | 'cmd' | 'shift' | 'alt')[];
  action: string;
  description: string;
  handler: () => void;
  context?: string; // Optional context where shortcut is active
  preventDefault?: boolean;
  stopPropagation?: boolean;
}

export interface AccessibilityFeatures {
  keyboardNavigation: boolean;
  screenReaderSupport: boolean;
  focusManagement: boolean;
  skipLinks: boolean;
  contrastCompliance: boolean;
  zoomSupport: boolean;
  reducedMotion: boolean;
}

export interface AccessibilityState {
  isHighContrast: boolean;
  prefersReducedMotion: boolean;
  screenReaderActive: boolean;
  keyboardNavigationMode: boolean;
  currentFocus: string | null;
  skipLinkTarget: string | null;
  announcements: string[];
  lastAnnouncement: string | null;
}

export interface FocusManagement {
  focusableElements: HTMLElement[];
  currentIndex: number;
  trapFocus: boolean;
  returnFocus: HTMLElement | null;
  customTabOrder?: string[];
}

export interface AriaLabels {
  [key: string]: string;
}

export interface ScreenReaderAnnouncement {
  message: string;
  priority: 'polite' | 'assertive';
  delay?: number;
}

// Keyboard shortcut definitions
export const KEYBOARD_SHORTCUTS: Record<
  string,
  Omit<KeyboardShortcut, 'handler'>
> = {
  SAVE_SONG: {
    key: 's',
    modifiers: ['ctrl'],
    action: 'SAVE_SONG',
    description: 'Save song',
    context: 'editor',
    preventDefault: true,
  },
  UNDO: {
    key: 'z',
    modifiers: ['ctrl'],
    action: 'UNDO',
    description: 'Undo last action',
    context: 'editor',
    preventDefault: true,
  },
  REDO: {
    key: 'y',
    modifiers: ['ctrl'],
    action: 'REDO',
    description: 'Redo last action',
    context: 'editor',
    preventDefault: true,
  },
  NEW_SONG: {
    key: 'n',
    modifiers: ['ctrl'],
    action: 'NEW_SONG',
    description: 'Create new song',
    context: 'editor',
    preventDefault: true,
  },
  OPEN_BROWSER: {
    key: 'o',
    modifiers: ['ctrl'],
    action: 'OPEN_BROWSER',
    description: 'Open song browser',
    context: 'editor',
    preventDefault: true,
  },
  LYRICS_MODE: {
    key: 'l',
    modifiers: ['ctrl'],
    action: 'LYRICS_MODE',
    description: 'Switch to lyrics mode',
    context: 'editor',
    preventDefault: true,
  },
  CHORD_MODE: {
    key: 'h',
    modifiers: ['ctrl'],
    action: 'CHORD_MODE',
    description: 'Switch to chord mode',
    context: 'editor',
    preventDefault: true,
  },
  TOGGLE_VOICE_RECORDING: {
    key: ' ',
    modifiers: [],
    action: 'TOGGLE_VOICE_RECORDING',
    description: 'Toggle voice recording',
    context: 'voice-modal',
    preventDefault: true,
  },
  CONFIRM_EDIT: {
    key: 'Enter',
    modifiers: [],
    action: 'CONFIRM_EDIT',
    description: 'Confirm edit',
    context: 'editing',
    preventDefault: true,
  },
  CANCEL_OPERATION: {
    key: 'Escape',
    modifiers: [],
    action: 'CANCEL_OPERATION',
    description: 'Cancel current operation',
    context: 'global',
    preventDefault: false,
  },
  MOVE_CHORD_LEFT: {
    key: 'ArrowLeft',
    modifiers: ['ctrl'],
    action: 'MOVE_CHORD_LEFT',
    description: 'Move selected chord left',
    context: 'chord-mode',
    preventDefault: true,
  },
  MOVE_CHORD_RIGHT: {
    key: 'ArrowRight',
    modifiers: ['ctrl'],
    action: 'MOVE_CHORD_RIGHT',
    description: 'Move selected chord right',
    context: 'chord-mode',
    preventDefault: true,
  },
  MOVE_CHORD_UP: {
    key: 'ArrowUp',
    modifiers: ['ctrl'],
    action: 'MOVE_CHORD_UP',
    description: 'Move selected chord to line above',
    context: 'chord-mode',
    preventDefault: true,
  },
  MOVE_CHORD_DOWN: {
    key: 'ArrowDown',
    modifiers: ['ctrl'],
    action: 'MOVE_CHORD_DOWN',
    description: 'Move selected chord to line below',
    context: 'chord-mode',
    preventDefault: true,
  },
};

// Common chord library
export const DEFAULT_CHORD_LIBRARY: ChordLibrary = {
  major: ['C', 'D', 'E', 'F', 'G', 'A', 'B'],
  minor: ['Cm', 'Dm', 'Em', 'Fm', 'Gm', 'Am', 'Bm'],
  seventh: ['C7', 'D7', 'E7', 'F7', 'G7', 'A7', 'B7'],
  minorSeventh: ['Cm7', 'Dm7', 'Em7', 'Fm7', 'Gm7', 'Am7', 'Bm7'],
  suspended: ['Csus2', 'Csus4', 'Dsus2', 'Dsus4', 'Esus2', 'Esus4'],
  extended: ['Cadd9', 'Dadd9', 'C9', 'C11', 'C13', 'Cmaj7', 'Dmaj7'],
  diminished: ['Cdim', 'Ddim', 'Edim', 'Fdim', 'Gdim', 'Adim', 'Bdim'],
  augmented: ['Caug', 'Daug', 'Eaug', 'Faug', 'Gaug', 'Aaug', 'Baug'],
  recent: [],
  favorites: [],
};
